import { BACKEND_URL } from '@/config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, BookOpen, FileText, Edit, Save, XCircle, Combine } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const ManuscriptOutlinePreview = ({ manuscriptId, onClose }) => {
  const [outline, setOutline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableChapters, setEditableChapters] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchOutline = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${BACKEND_URL}/api/manuscripts/${manuscriptId}/outline`
        );
        setOutline(response.data);
        // Initialize editable chapters
        setEditableChapters(JSON.parse(JSON.stringify(response.data.chapters || [])));
      } catch (err) {
        console.error('Error fetching outline:', err);
        setError(err.response?.data?.detail || 'Failed to load chapter outline');
      } finally {
        setLoading(false);
      }
    };

    fetchOutline();
  }, [manuscriptId]);

  const handleEditMode = () => {
    setIsEditMode(true);
    setEditableChapters(JSON.parse(JSON.stringify(outline.chapters || [])));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditableChapters(JSON.parse(JSON.stringify(outline.chapters || [])));
  };

  const updateChapterField = (index, field, value) => {
    const updated = [...editableChapters];
    updated[index][field] = value;
    setEditableChapters(updated);
  };

  const handleMergeWithNext = (index) => {
    if (index >= editableChapters.length - 1) return;

    const current = editableChapters[index];
    const next = editableChapters[index + 1];

    // Create merged chapter
    const merged = {
      index: current.index,
      label: current.label || next.label || `Chapter ${index + 1}`,
      title: current.title && next.title
        ? `${current.title} / ${next.title}`
        : current.title || next.title || '',
      start_char_index: current.start_char_index,
      end_char_index: next.end_char_index,
      estimated_word_count: (current.estimated_word_count || 0) + (next.estimated_word_count || 0)
    };

    // Replace both chapters with merged one
    const updated = [
      ...editableChapters.slice(0, index),
      merged,
      ...editableChapters.slice(index + 2)
    ];

    setEditableChapters(updated);
    toast.success(`Merged "${current.label}" with "${next.label}"`);
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);

    try {
      // Validate labels are not empty
      for (let i = 0; i < editableChapters.length; i++) {
        if (!editableChapters[i].label || !editableChapters[i].label.trim()) {
          toast.error(`Chapter ${i + 1} label cannot be empty`);
          setIsSaving(false);
          return;
        }
      }

      // Build payload
      const payload = {
        document_type: outline.document_type || 'single_book',
        chapters: editableChapters.map((ch, idx) => ({
          index: idx,
          label: ch.label || `Chapter ${idx + 1}`,
          title: ch.title || '',
          start_char_index: ch.start_char_index,
          end_char_index: ch.end_char_index
        }))
      };

      // Send PATCH request
      const response = await axios.patch(
        `${BACKEND_URL}/api/manuscripts/${manuscriptId}/outline`,
        payload
      );

      // Update local state with response
      setOutline(response.data);
      setEditableChapters(response.data.chapters);
      setIsEditMode(false);

      toast.success('Chapter outline updated successfully!');
    } catch (err) {
      console.error('Error saving outline:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to save outline changes';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-3xl max-h-[80vh] overflow-hidden">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Chapter Outline
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading chapters...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-3xl">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <CardTitle>Error</CardTitle>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-center py-6">
              <p className="text-red-600">{error}</p>
              <Button onClick={onClose} className="mt-4">Close</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const chapters = outline?.chapters || [];
  const displayChapters = isEditMode ? editableChapters : chapters;
  const isSingleChapter = chapters.length === 1 && chapters[0].label === "Full Manuscript";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <CardHeader className="border-b bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              {isEditMode ? 'Edit Chapter Outline' : 'Chapter Outline'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="text-sm text-teal-100 mt-2">
            {displayChapters.length} {displayChapters.length === 1 ? 'section' : 'chapters'} 
            {isEditMode && ' (editing)'}
          </div>
        </CardHeader>

        <CardContent className="p-6 overflow-y-auto flex-1">
          {isSingleChapter && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-3">
                <FileText className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900 mb-1">No Explicit Chapter Headings Detected</p>
                  <p className="text-sm text-amber-800">
                    This manuscript will be treated as a single continuous section for audio playback.
                    If your manuscript has chapters, they may not be using standard formatting (e.g., "Chapter 1", "Prologue", etc.).
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {displayChapters.map((chapter, index) => (
              <div
                key={index}
                className={`p-4 border rounded-lg transition-colors ${
                  isEditMode ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    {isEditMode ? (
                      // Edit Mode
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium text-gray-700 mb-1 block">
                            Chapter Label *
                          </label>
                          <Input
                            value={chapter.label}
                            onChange={(e) => updateChapterField(index, 'label', e.target.value)}
                            placeholder="e.g., Chapter 1, Prologue"
                            className="text-base"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-700 mb-1 block">
                            Chapter Title (optional)
                          </label>
                          <Input
                            value={chapter.title || ''}
                            onChange={(e) => updateChapterField(index, 'title', e.target.value)}
                            placeholder="e.g., The Beginning"
                            className="text-base"
                          />
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            ~{chapter.estimated_word_count?.toLocaleString() || 0} words
                          </span>
                        </div>
                        {index < displayChapters.length - 1 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMergeWithNext(index)}
                            className="mt-2"
                            disabled={isSaving}
                          >
                            <Combine className="w-4 h-4 mr-2" />
                            Merge with Next Chapter
                          </Button>
                        )}
                      </div>
                    ) : (
                      // View Mode
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 text-lg mb-1">
                            {chapter.label}
                          </h3>
                          {chapter.title && chapter.title !== chapter.label && (
                            <p className="text-gray-600 mb-2">{chapter.title}</p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              ~{chapter.estimated_word_count?.toLocaleString() || 0} words
                            </span>
                            {!isSingleChapter && (
                              <span className="text-xs">
                                Position: {chapter.start_char_index}-{chapter.end_char_index}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {chapters.length > 1 && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Audio will be generated based on these detected chapters. Each chapter will be a separate audio segment for easy navigation.
              </p>
            </div>
          )}
        </CardContent>

        <div className="border-t p-4 bg-gray-50 flex justify-between items-center">
          <div>
            {!isEditMode && !isSingleChapter && (
              <Button
                variant="outline"
                onClick={handleEditMode}
                className="flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit Structure
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            {isEditMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={onClose} className="px-6">
                Close
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ManuscriptOutlinePreview;
